<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchSite"
      >
        <template #start>
          <vs-button
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            class="button shadow-primary font-semibold"
            color="primary"
            @click="onHandleAdd"
            :disabled="isBusy"
          >
            Add New Site
          </vs-button>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :table-header="header"
        :table-data="sites"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        body-height="auto"
        :loading="isBusy"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template v-if="row.isEditing">
              <div
                :class="`flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'animate-bounce opacity-50 cursor-not-allowed'}`"
                @click="isProcessing ? null : onHandleSubmit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="save"
                >
              </div>
              <div
                :class="`flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'animate-bounce opacity-50 cursor-not-allowed'}`"
                @click="isProcessing ? null : onHandleCancel(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="cancel"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="onHandleEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-edit.svg')"
                  alt="save"
                >
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="onHandleDelete(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-delete.svg')"
                  alt="cancel"
                >
              </div>
            </template>
          </div>
        </template>
        <template #name="{ row }">
          <div v-if="row.isEditing">
            <vs-input
              :disabled="isProcessing"
              :danger="!!!row.editForm.name || validate === 'name'"
              :danger-text="validate ? 'Name already exist' : '* Required'"
              v-model="row.editForm.name"
              placeholder="Enter site name"
            />
          </div>
          <div v-else>
            {{ row.name || '-' }}
          </div>
        </template>
        <template #created_by="{ row }">
          <div>
            {{ row.isEditing ? '-' : row.created_by }}
          </div>
        </template>
        <template #created_at="{ row }">
          <div>
            {{ row.isEditing ? '-' : moment(row.created_at).format('DD MMM YYYY HH:mm') }}
          </div>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchSite"
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      is-warning
      title="Delete Site"
      left-button="Cancel"
      right-button="Yes, Delete"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null, tempName: '' }"
      @leftButton="popUp = { isShown: false, tempId: null, tempName: '' }"
      @rightButton="deleteSite"
    >
      <p class="text-center mt-1">
        Are you sure want to delete
      </p>
      <p class="text-center">
        "{{ popUp.tempName }}"?
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUpSuccess"
      @close="resetTableFilter"
      @ok="resetTableFilter"
      title="Success"
    >
      Site successfully deleted.
    </pop-up>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'

import downloadUtils from '../../../utils/downloadUtils'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    DataTable,
    TableBottom,
    TableTop,
    PopUp
  },
  data: () => ({
    header: [
      { key: 'no', label: 'No', sortable: false, width: '50px' },
      { key: 'name', label: 'Name', sortable: true },
      { key: 'created_by', label: 'Created By', sortable: true },
      { key: 'created_at', label: 'Created Time', sortable: true }
    ],
    filters: {
      sort_by: 'name',
      sort_dir: 'asc'
    },
    popUp: {
      isShown: false,
      tempId: null,
      tempName: ''
    },
    popUpSuccess: false,
    isAdding: false,
    isBusy: false,
    sites: [],
    validate: '',
    isDownload: false,
    isProcessing: false
  }),
  computed: {
    ...mapGetters({
      siteList: 'SiteManagement/siteList',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      fileExport: 'SiteManagement/fileExport'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  watch: {
    siteList: {
      handler(val) {
        if (val?.data) {
          this.sites = val.data.map(item => ({
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadFileExport();
        }
      }
    },
    fileExport: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Sites.xlsx`)
          // Reset the export file state
          this.$store.dispatch('SiteManagement/resetFileExport');
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.header.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.header.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.header.splice(index, 1);
        }
      }
    }
  },
  mounted() {
    this.fetchSite()
  },
  methods: {
    async fetchSite() {
      try {
        this.isBusy = true
        const apiFilters = {
          is_list: true,
          page: this.currentPage || 1,
          limit: this.pageSize || 10
        };

        await this.$store.dispatch('SiteManagement/fetchSite', apiFilters)

        this.isAdding = false
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch Site Management list, please try again')
      }
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy;
      this.filters.sort_dir = sortDir;
      this.fetchSite();
    },
    async deleteSite() {
      if (this.isAdding || this.sites.some(r => r.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. Please save your changes first', 'Error')
        return
      }
      this.isProcessing = true;
      try {
        await this.$store.dispatch('SiteManagement/deleteSite', this.popUp.tempId)

        this.popUp = { isShown: false, tempId: null, tempName: '' }
        this.isProcessing = false;
        this.popUpSuccess = true
      } catch (e) {
        this.isProcessing = false;
        this.showFailedNotification(this, 'Failed to delete Site Management, please try again')
      }
    },
    async resetTableFilter() {
      await this.$store.dispatch('Table/resetConfig')
      await this.fetchSite()
      this.popUpSuccess = false
    },
    onHandleAdd() {
      // Validate if have active action
      if (this.isAdding || this.sites.some(row => row.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. Please save your changes first', 'Error')
        return
      }

      // Create new row
      const newRow = {
        id: `new-${  Date.now()}`, // Temporary ID for new row
        isEditing: true,
        editForm: {
          name: ''
        }
      }

      this.isAdding = true
      this.sites.unshift(newRow)
    },
    onHandleCancel(row) {
      if (this.isAdding) {
        // Remove the new row if we're canceling an add
        const index = this.sites.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.sites.splice(index, 1)
        }
      } else {
        // Just cancel the edit
        const index = this.sites.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.sites[index].isEditing = false
          this.$set(this.sites[index], 'isEditing', false)
        }
      }
      this.isAdding = false
    },
    async onHandleSubmit(row) {
      // Validate fields
      const { editForm } = row
      const validate = Object.keys(editForm).find(key => editForm[key] === '')

      if (validate) {
        this.showFailedNotification(this, `Site ${validate} cannot be empty`, 'Error')
        return
      }
      try {
        const { isEditing, ...dataForm } = row

        this.isProcessing = true;
        if (this.isAdding ) {
        // Handle add
          await this.$store.dispatch('SiteManagement/postSite', dataForm.editForm)
          this.showSuccessNotification(this, 'Site successfully added.')
        } else {
        // Handle edit
          const { id, editForm } = row
          await this.$store.dispatch('SiteManagement/putSite', {
            id: id,
            form: editForm
          })
          this.showSuccessNotification(
            this,
            `Site successfully updated.`
          )
        }

        // Close popup, cancel edit and refetch list
        this.cancelAllEdits()
        this.fetchSite()
        this.isProcessing = false;
      } catch ({ error }) {
        this.isProcessing = false;
        let message = error?.errors?.[0]?.[0]?.message || error?.message || 'Failed to save changes, please try again'

        // set validate state & error message for value that already exist
        if (error?.message?.includes('already')) {
          if (error?.message?.includes('name')) {
            this.validate = 'name'
            message = 'Site Name already exists'
          }
        }

        this.showFailedNotification(this, message, 'Error')
      }
    },
    onHandleDelete(row) {
      if (this.isAdding || this.sites.some(row => row.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. Please save your changes first', 'Error')
        return
      }

      this.popUp = { isShown: true, tempId: row.id, tempName: row.name }
    },
    cancelAllEdits() {
      // Remove Index 1 For isAdding
      if (this.sites.length > 10) {
        this.sites.splice(0, 1)
      }

      // Cancel any existing edit
      this.sites.map(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false)
        }
      })
      this.isAdding = false
    },
    onHandleEdit(row) {
      // Cancel any existing edit or add
      if (this.isAdding || this.sites.some(r => r.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. Please save your changes first', 'Error')
        return
      }

      // Populate Edit Form
      const index = this.sites.findIndex(item => item.id === row.id)
      if (index !== -1) {
        this.$set(this.sites[index], 'isEditing', true)
        this.$set(this.sites[index], 'editForm', {
          id: row.id,
          name: row.name
        })
      }
    },
    async downloadFileExport() {
      try {
        this.isBusy = true
        await this.$store.dispatch('SiteManagement/downloadFileExport', {
          is_list: true,
          sort_dir: this.filters.sort_dir || 'asc',
          sort_by: this.filters.sort_by || 'name'
        }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isBusy = false
        this.isDownload = false;
      } catch (error) {
        this.isDownload = false;
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to download file, please try again');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  @apply bg-primary;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.badge-status {
  @apply w-20;
  text-align: center;
  padding: 5px 0;
  border-radius: 5px;

  &.active {
    @apply bg-green-200 text-green-700 border-green-400
  }

  &.inactive {
    @apply bg-red-200 text-red-700 border-red-400
  }
}
</style>
